/* Weekly + monthly calendar view for the Admin panel.
   No external deps. Reuses the same AppointmentEditor modal already in AdminPage.jsx.
*/
import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus, FileText } from "lucide-react";
import { exportTherapistDayPdf } from "@/utils/pdf";

const SERVICE_COLOR = {
  terapia_fisica:        { bg: "bg-green-100",   border: "border-green-300",   text: "text-green-900",   dot: "bg-green-500"   },
  rehabilitacion:        { bg: "bg-green-100",   border: "border-green-300",   text: "text-green-900",   dot: "bg-green-500"   },
  quiropractica:         { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-900", dot: "bg-emerald-500" },
  terapias_alternativas: { bg: "bg-amber-100",   border: "border-amber-300",   text: "text-amber-900",   dot: "bg-amber-500"   },
  estetica:              { bg: "bg-rose-100",    border: "border-rose-300",    text: "text-rose-900",    dot: "bg-rose-500"    },
  consulta_general:      { bg: "bg-slate-100",   border: "border-slate-300",   text: "text-slate-900",   dot: "bg-slate-500"   },
};

// Therapist groupings — used by the Day view
const THERAPISTS = [
  {
    id: "omar",
    name: "Omar Aguilera",
    role: "Fisio · Quiro",
    accent: "green",
    services: ["terapia_fisica", "rehabilitacion", "quiropractica"],
  },
  {
    id: "ismael",
    name: "Lic. Ismael Mendoza",
    role: "Alternativas · Funcional",
    accent: "amber",
    services: ["terapias_alternativas", "consulta_general"],
  },
  {
    id: "anabel",
    name: "Lic. Anabel Mendoza",
    role: "Estética",
    accent: "rose",
    services: ["estetica"],
  },
];

const THERAPIST_HEAD = {
  green: "bg-green-50 border-green-200 text-green-900",
  amber: "bg-amber-50 border-amber-200 text-amber-900",
  rose: "bg-rose-50 border-rose-200 text-rose-900",
};

const SERVICE_LABEL = {
  terapia_fisica: "Terapia Física",
  rehabilitacion: "Rehabilitación",
  quiropractica: "Quiropráctica",
  terapias_alternativas: "T. Alternativas",
  estetica: "Estética",
  consulta_general: "Consulta",
};

const TIME_SLOTS = ["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30","19:00","19:30"];

const dayLabels = ["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"];
const monthLabels = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const startOfWeek = (d) => {
  const x = new Date(d);
  const diff = (x.getDay() + 6) % 7; // Mon = 0
  x.setDate(x.getDate() - diff);
  x.setHours(0, 0, 0, 0);
  return x;
};

const fmtDateISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const isToday = (d) => fmtDateISO(d) === fmtDateISO(new Date());

// Returns the tailwind text-color class for a monthly-calendar day number.
const monthCellNumberColor = (d, inMonth) => {
  if (isToday(d)) return "text-green-700";
  if (inMonth) return "text-slate-700";
  return "text-slate-300";
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const Legend = () => (
  <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
    {Object.entries(SERVICE_LABEL).map(([k, v]) => (
      <span key={k} className="inline-flex items-center gap-1.5">
        <span className={`h-2.5 w-2.5 rounded-full ${SERVICE_COLOR[k]?.dot || "bg-slate-400"}`} />
        {v}
      </span>
    ))}
  </div>
);

const ServiceFilter = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
    data-testid="cal-service-filter"
  >
    <option value="">Todos los servicios</option>
    {Object.entries(SERVICE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
  </select>
);

// -------------------- Week view --------------------
const WeekView = ({ anchor, appts, onCreate, onEdit, onMove, filter }) => {
  const start = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const [dragging, setDragging] = useState(null); // appointment being dragged
  const [hoverCell, setHoverCell] = useState(null); // "date__time"

  // Group appointments by date+time
  const byCell = useMemo(() => {
    const map = {};
    for (const a of appts) {
      if (filter && a.service !== filter) continue;
      const key = `${a.date}__${a.time}`;
      (map[key] = map[key] || []).push(a);
    }
    return map;
  }, [appts, filter]);

  const onDragStart = (a) => (e) => {
    setDragging(a);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", a.id);
  };
  const onDragOver = (date, time) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setHoverCell(`${date}__${time}`);
  };
  const onDropCell = (date, time) => (e) => {
    e.preventDefault();
    if (!dragging) return;
    setHoverCell(null);
    if (dragging.date === date && dragging.time === time) { setDragging(null); return; }
    onMove(dragging, { date, time });
    setDragging(null);
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-100 overflow-x-auto" data-testid="cal-week-view">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-50">
            <th className="w-20 px-3 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Hora</th>
            {days.map((d) => (
              <th key={d.toISOString()} className={`px-2 py-3 text-center border-l border-slate-100 ${isToday(d) ? "bg-green-50" : ""}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{dayLabels[(d.getDay() + 6) % 7]}</div>
                <div className={`mt-0.5 text-base font-bold ${isToday(d) ? "text-green-700" : "text-slate-900"}`}>{d.getDate()}</div>
                <div className="text-[10px] text-slate-400">{monthLabels[d.getMonth()].slice(0, 3)}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {TIME_SLOTS.map((t) => (
            <tr key={t} className="border-t border-slate-100">
              <td className="px-3 py-2 text-[11px] text-slate-500 font-medium align-top">{t}</td>
              {days.map((d) => {
                const dateISO = fmtDateISO(d);
                const cellKey = `${dateISO}__${t}`;
                const cellAppts = byCell[cellKey] || [];
                const isHover = hoverCell === cellKey && dragging;
                return (
                  <td
                    key={d.toISOString() + t}
                    className={`relative px-1 py-1 border-l border-slate-100 align-top min-w-[120px] ${isToday(d) ? "bg-green-50/40" : ""} ${isHover ? "bg-blue-50 ring-2 ring-blue-300 ring-inset" : ""}`}
                    data-testid={`cal-cell-${dateISO}-${t}`}
                    onDragOver={onDragOver(dateISO, t)}
                    onDragLeave={() => hoverCell === cellKey && setHoverCell(null)}
                    onDrop={onDropCell(dateISO, t)}
                  >
                    {cellAppts.length === 0 ? (
                      <button
                        type="button"
                        onClick={() => onCreate(dateISO, t)}
                        className="w-full h-full min-h-[36px] rounded-md border border-dashed border-transparent hover:border-green-300 hover:bg-green-50/40 transition-colors text-slate-300 hover:text-green-600 grid place-items-center text-[10px]"
                        title="Crear cita en este horario"
                      >
                        <Plus className="h-3 w-3 opacity-0 hover:opacity-100" />
                      </button>
                    ) : (
                      <div className="space-y-1">
                        {cellAppts.map((a) => {
                          const c = SERVICE_COLOR[a.service] || SERVICE_COLOR.consulta_general;
                          return (
                            <div
                              key={a.id}
                              draggable
                              onDragStart={onDragStart(a)}
                              onDragEnd={() => { setDragging(null); setHoverCell(null); }}
                              onClick={() => onEdit(a)}
                              className={`cursor-move rounded-md ${c.bg} ${c.border} ${c.text} border px-1.5 py-1 hover:brightness-95 ${dragging?.id === a.id ? "opacity-40" : ""}`}
                              data-testid={`cal-appt-${a.id}`}
                              title={`${a.name} · ${SERVICE_LABEL[a.service] || a.service} · ${a.phone}\n(Arrastra para mover, click para editar)`}
                            >
                              <div className="font-semibold truncate text-[11px] leading-tight">{a.name}</div>
                              <div className="text-[9px] opacity-80 truncate">{SERVICE_LABEL[a.service] || a.service}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// -------------------- Day view (per therapist) --------------------
const DayView = ({ anchor, appts, onCreate, onEdit, onMove, filter }) => {
  const dateISO = fmtDateISO(anchor);
  const [dragging, setDragging] = useState(null);
  const [hoverCell, setHoverCell] = useState(null); // "therapistId__time"

  // Group: therapist -> time -> appts
  const byTherapistTime = useMemo(() => {
    const map = {};
    for (const t of THERAPISTS) map[t.id] = {};
    for (const a of appts) {
      if (a.date !== dateISO) continue;
      if (filter && a.service !== filter) continue;
      const therapist = THERAPISTS.find((th) => th.services.includes(a.service));
      const id = therapist?.id || "ismael";
      (map[id][a.time] = map[id][a.time] || []).push(a);
    }
    return map;
  }, [appts, dateISO, filter]);

  const totalsByTherapist = useMemo(() => {
    const out = {};
    for (const t of THERAPISTS) {
      out[t.id] = appts.filter((a) => a.date === dateISO && t.services.includes(a.service)).length;
    }
    return out;
  }, [appts, dateISO]);

  const onDragStart = (a) => (e) => {
    setDragging(a);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", a.id);
  };
  const onDragOver = (key) => (e) => {
    e.preventDefault();
    setHoverCell(key);
  };
  const onDropCell = (time, therapist) => (e) => {
    e.preventDefault();
    if (!dragging) return;
    setHoverCell(null);
    // Determine target service (keep current if compatible, else pick therapist's primary)
    const keep = therapist.services.includes(dragging.service);
    const newService = keep ? dragging.service : therapist.services[0];
    onMove(dragging, { date: dateISO, time, service: newService });
    setDragging(null);
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-100 overflow-x-auto" data-testid="cal-day-view">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-50">
            <th className="w-20 px-3 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Hora</th>
            {THERAPISTS.map((t) => {
              const therapistAppts = appts.filter((a) => a.date === dateISO && t.services.includes(a.service));
              return (
              <th key={t.id} className={`px-3 py-3 text-left border-l border-slate-100 ${THERAPIST_HEAD[t.accent]}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-bold">{t.name}</div>
                    <div className="text-[10px] opacity-80 font-medium uppercase tracking-widest mt-0.5">{t.role}</div>
                    <div className="text-[10px] mt-1">
                      <span className="font-semibold">{totalsByTherapist[t.id]}</span> cita{totalsByTherapist[t.id] === 1 ? "" : "s"} hoy
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => exportTherapistDayPdf({ therapist: t, date: dateISO, appointments: therapistAppts })}
                    disabled={therapistAppts.length === 0}
                    className="rounded-full bg-white/80 hover:bg-white px-2.5 py-1 text-[10px] font-bold inline-flex items-center gap-1 border border-current disabled:opacity-40 disabled:cursor-not-allowed"
                    title={`Exportar agenda de ${t.name.split(" ")[0]} en PDF`}
                    data-testid={`cal-day-pdf-${t.id}`}
                  >
                    <FileText className="h-3 w-3" /> PDF
                  </button>
                </div>
              </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {TIME_SLOTS.map((t) => (
            <tr key={t} className="border-t border-slate-100">
              <td className="px-3 py-2 text-[11px] text-slate-500 font-medium align-top">{t}</td>
              {THERAPISTS.map((th) => {
                const cellKey = `${th.id}__${t}`;
                const cellAppts = byTherapistTime[th.id][t] || [];
                const isHover = hoverCell === cellKey && dragging;
                return (
                  <td
                    key={th.id + t}
                    className={`relative px-1.5 py-1.5 border-l border-slate-100 align-top min-w-[200px] ${isHover ? "bg-blue-50 ring-2 ring-blue-300 ring-inset" : ""}`}
                    data-testid={`cal-day-cell-${th.id}-${t}`}
                    onDragOver={onDragOver(cellKey)}
                    onDragLeave={() => hoverCell === cellKey && setHoverCell(null)}
                    onDrop={onDropCell(t, th)}
                  >
                    {cellAppts.length === 0 ? (
                      <button
                        type="button"
                        onClick={() => onCreate(dateISO, t, th.services[0])}
                        className="w-full min-h-[40px] rounded-md border border-dashed border-transparent hover:border-green-300 hover:bg-green-50/40 transition-colors text-slate-300 hover:text-green-600 grid place-items-center"
                        title={`Crear cita para ${th.name}`}
                      >
                        <Plus className="h-3.5 w-3.5 opacity-0 hover:opacity-100" />
                      </button>
                    ) : (
                      <div className="space-y-1.5">
                        {cellAppts.map((a) => {
                          const c = SERVICE_COLOR[a.service] || SERVICE_COLOR.consulta_general;
                          return (
                            <div
                              key={a.id}
                              draggable
                              onDragStart={onDragStart(a)}
                              onDragEnd={() => { setDragging(null); setHoverCell(null); }}
                              onClick={() => onEdit(a)}
                              className={`cursor-move rounded-lg ${c.bg} ${c.border} ${c.text} border px-2.5 py-2 hover:brightness-95 ${dragging?.id === a.id ? "opacity-40" : ""}`}
                              data-testid={`cal-day-appt-${a.id}`}
                            >
                              <div className="font-semibold text-[12px] leading-tight">{a.name}</div>
                              <div className="text-[10px] opacity-90 mt-0.5">{SERVICE_LABEL[a.service] || a.service}</div>
                              <div className="text-[10px] opacity-80 mt-0.5 truncate">📞 {a.phone}</div>
                              {a.notes && <div className="text-[10px] opacity-70 mt-1 truncate italic">"{a.notes}"</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// -------------------- Month view --------------------
const MonthView = ({ anchor, appts, onCreate, onEdit, filter }) => {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const gridStart = startOfWeek(firstOfMonth);

  // Build 6-week grid
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

  const byDay = useMemo(() => {
    const map = {};
    for (const a of appts) {
      if (filter && a.service !== filter) continue;
      (map[a.date] = map[a.date] || []).push(a);
    }
    // sort by time
    Object.values(map).forEach((arr) => arr.sort((x, y) => x.time.localeCompare(y.time)));
    return map;
  }, [appts, filter]);

  return (
    <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden" data-testid="cal-month-view">
      <div className="grid grid-cols-7 bg-slate-50">
        {dayLabels.map((d) => (
          <div key={d} className="px-2 py-2 text-center text-[10px] uppercase tracking-widest text-slate-500 font-semibold border-r border-slate-100 last:border-r-0">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === month;
          const dateISO = fmtDateISO(d);
          const cellAppts = byDay[dateISO] || [];
          return (
            <div
              key={i}
              className={`min-h-[110px] border-r border-b border-slate-100 last:border-r-0 p-1.5 flex flex-col ${
                inMonth ? "bg-white" : "bg-slate-50/50 text-slate-300"
              } ${isToday(d) ? "ring-2 ring-green-300 ring-inset" : ""}`}
              data-testid={`cal-month-cell-${dateISO}`}
            >
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => onCreate(dateISO, "10:00")}
                  className={`text-xs font-semibold ${monthCellNumberColor(d, inMonth)} hover:text-green-700`}
                  title="Crear cita"
                >
                  {d.getDate()}
                </button>
                {cellAppts.length > 0 && (
                  <span className="text-[9px] text-slate-400 font-medium">{cellAppts.length}</span>
                )}
              </div>
              <div className="mt-1 space-y-0.5 flex-1 overflow-hidden">
                {cellAppts.slice(0, 3).map((a) => {
                  const c = SERVICE_COLOR[a.service] || SERVICE_COLOR.consulta_general;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => onEdit(a)}
                      className={`w-full text-left rounded-sm ${c.bg} ${c.text} text-[10px] px-1 py-0.5 truncate flex items-center gap-1`}
                      title={`${a.time} · ${a.name}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${c.dot} flex-shrink-0`} />
                      <span className="font-medium">{a.time}</span>
                      <span className="truncate">{a.name}</span>
                    </button>
                  );
                })}
                {cellAppts.length > 3 && (
                  <div className="text-[9px] text-slate-500 font-medium pl-1">+{cellAppts.length - 3} más</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// -------------------- Main wrapper --------------------
export const CalendarView = ({ appointments, onCreate, onEdit, onMove }) => {
  const [mode, setMode] = useState("week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [filter, setFilter] = useState("");

  const shift = (dir) => {
    if (mode === "week") setAnchor(addDays(anchor, dir * 7));
    else if (mode === "day") setAnchor(addDays(anchor, dir));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
  };

  const goToday = () => setAnchor(new Date());

  const headerLabel = (() => {
    if (mode === "week") {
      const s = startOfWeek(anchor);
      const e = addDays(s, 6);
      const sm = monthLabels[s.getMonth()];
      const em = monthLabels[e.getMonth()];
      if (s.getMonth() === e.getMonth()) return `${s.getDate()}–${e.getDate()} ${sm} ${e.getFullYear()}`;
      return `${s.getDate()} ${sm.slice(0, 3)} – ${e.getDate()} ${em.slice(0, 3)} ${e.getFullYear()}`;
    }
    if (mode === "day") {
      return `${dayLabels[(anchor.getDay() + 6) % 7]} ${anchor.getDate()} de ${monthLabels[anchor.getMonth()]} ${anchor.getFullYear()}`;
    }
    return `${monthLabels[anchor.getMonth()]} ${anchor.getFullYear()}`;
  })();

  return (
    <div className="mt-6" data-testid="calendar-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-green-600" /> Calendario de citas
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Arrastra una cita para moverla · Click en un horario vacío para crear · Click en una cita para editar
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ServiceFilter value={filter} onChange={setFilter} />
          <div className="inline-flex rounded-full border border-slate-200 bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setMode("day")}
              className={`px-3 py-1.5 text-xs font-semibold ${mode === "day" ? "bg-green-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
              data-testid="cal-mode-day"
            >
              Día
            </button>
            <button
              type="button"
              onClick={() => setMode("week")}
              className={`px-3 py-1.5 text-xs font-semibold ${mode === "week" ? "bg-green-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
              data-testid="cal-mode-week"
            >
              Semana
            </button>
            <button
              type="button"
              onClick={() => setMode("month")}
              className={`px-3 py-1.5 text-xs font-semibold ${mode === "month" ? "bg-green-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
              data-testid="cal-mode-month"
            >
              Mes
            </button>
          </div>
          <div className="inline-flex items-center gap-1">
            <button type="button" onClick={() => shift(-1)} className="h-8 w-8 rounded-full border border-slate-200 bg-white grid place-items-center text-slate-600 hover:bg-slate-50" data-testid="cal-prev">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button type="button" onClick={goToday} className="px-3 h-8 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="cal-today">
              Hoy
            </button>
            <button type="button" onClick={() => shift(1)} className="h-8 w-8 rounded-full border border-slate-200 bg-white grid place-items-center text-slate-600 hover:bg-slate-50" data-testid="cal-next">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="font-semibold text-slate-800 capitalize" data-testid="cal-header-label">{headerLabel}</p>
        <Legend />
      </div>

      {mode === "day" && (
        <DayView anchor={anchor} appts={appointments} onCreate={onCreate} onEdit={onEdit} onMove={onMove} filter={filter} />
      )}
      {mode === "week" && (
        <WeekView anchor={anchor} appts={appointments} onCreate={onCreate} onEdit={onEdit} onMove={onMove} filter={filter} />
      )}
      {mode === "month" && (
        <MonthView anchor={anchor} appts={appointments} onCreate={onCreate} onEdit={onEdit} filter={filter} />
      )}
    </div>
  );
};

export default CalendarView;
